import { CdkTrapFocus } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
  ViewEncapsulation,
} from "@angular/core";
import { MatCheckbox, MatCheckboxChange } from "@angular/material/checkbox";
import { MatMenuTrigger } from "@angular/material/menu";
import { Subscription } from "rxjs";
import { translateAndFormat } from "src/app/i18next";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";
import { SearchInputComponent } from "../search-input/search-input.component";
export interface Option {
	label: string;
	value: string;
	icon?: string;
}

export interface CheckboxOption extends Option {
	checkboxElement?: MatCheckbox;
}
export interface DropdownMultiSelectState {
	dropdownMultiSelectLabel: string;
	potentialDropdownMultiSelectLabel: string;
	buttonIconValue: string;
	optionsSelected: Array<CheckboxOption>;
	showLabel: boolean;
}

@Component({
	selector: "app-multi-select-dropdown",
	templateUrl: "./multi-select-dropdown.component.html",
	styleUrls: ["./multi-select-dropdown.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class MultiSelectDropdownComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	/**
	 * Dropdown context, "filter type: view".
	 * For example: asset-type-filter inspection-history.
	 */
	@Input() context: string;
	/**
	 * Dropdown label
	 */
	@Input() dropdownLabel: string;
	/**
	 * Dropdown Multi Select options
	 */
	@Input() options: ReadonlyArray<Option> = [];
	/**
	 * Dropdown multi-select boolean, true by default
	 */
	@Input() isMultiSelect = true;
	/**
	 * Dropdown selected item, only has value if isMultiSelect = false
	 */

	/**
	 * Output - Submit options selected
	 */
	@Output() submitDropdownMultiSelect = new EventEmitter<Array<string>>();

	// Output - Submit single selected option
	@Output() submitDropdownSingleSelect = new EventEmitter<string>();

	/**
	 *
	 *
	 * View Children - Mat Checkbox
	 */
	@ViewChildren(MatCheckbox) checkboxes: QueryList<MatCheckbox>;

	@ViewChild(SearchInputComponent) searchInput: SearchInputComponent;
	@ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
	@ViewChild(CdkTrapFocus) trapFocus: CdkTrapFocus;
	@ViewChild("dialogWrapper") dialogWrapperRef: ElementRef<HTMLElement>;

	buttonIconValue = "arrow_drop_down";
	showMenu = false;
	defaultCheckState: Record<string, boolean> = {};
	defaultState: DropdownMultiSelectState = {
		dropdownMultiSelectLabel: "",
		potentialDropdownMultiSelectLabel: "",
		buttonIconValue: "arrow_drop_down",
		optionsSelected: [],
		showLabel: false,
	};
	filterButtonClass = "filter-button-not-focused";
	filterButtonLabelClass = "filter-button-label-not-focused";
	filterButtonTextClass = "filter-button-text-value-not-selected";
	potentialFilterButtonText = "";
	potentiallySelectedOptions: CheckboxOption[] = [];
	selectedOption = "";
	selectedOptions: CheckboxOption[] = [];
	showLabel = false;
	state: DropdownMultiSelectState;
	stateKey: string;
	subscriptions = new Subscription();
	originalOptions: Array<CheckboxOption>;
	sortedOptions: Array<CheckboxOption>;
	sortedOptionsBySearch: Array<CheckboxOption>;
	singleSelectedItem: any = null;
	search: string = null;
	sortedBySearchIds: { [key: string]: boolean } = {};
	inputStr: string = null;
	resetInput = false;

	instanceId = Math.random().toString(36).substring(7);

	constructor(public memCacheService: MemCacheService, @Inject(DOCUMENT) private readonly document: Document) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.options) {
			this.updateSortedOptions();
		}
	}

	/**
	 * On Init
	 * Subscribe to mem cache service to get the state of the dropdownå
	 */
	ngOnInit() {
		this.defaultState.dropdownMultiSelectLabel = this.dropdownLabel;

		this.subscriptions.add(
			this.memCacheService.cleared$().subscribe(() => {
				this.memCacheService.setValue(this.stateKey, null);

				this.captureSelection();

				this.submitDropdownMultiSelect.emit([]);
			}),
		);

		// sort the options
		this.updateSortedOptions();
	}

	clearSelected(event: MouseEvent) {
		event.stopPropagation();
		this.potentiallySelectedOptions = [];
		this.checkboxes.forEach(box => {
			box.checked = false;
		});
	}

	updateSortedOptions() {
		if (this.options) {
			this.sortedOptions = [...this.options].sort(({ label: labelA }, { label: labelB }) =>
				labelA.toLocaleLowerCase().localeCompare(labelB.toLocaleLowerCase()),
			);
			this.originalOptions = [...this.sortedOptions];
		}
	}
	/**
	 * After view Init
	 * Defer captureSelection until after the view detection cycle has completed
	 */
	ngAfterViewInit() {
		setTimeout(() => {
			this.captureSelection();
		});
	}

	/**
	 * On Destroy
	 * Unsubscribe from all subscriptions
	 */
	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	/**
	 * Initialize the component state
	 */
	initState() {
		this.stateKey = this.context;

		this.state = this.getState();

		if (!this.state) {
			this.state = {
				...this.defaultState,
			};
		}

		this.state.optionsSelected.forEach(option => {
			option.checkboxElement = this.checkboxes.find(checkbox => checkbox.id === option.value);
		});

		this.showLabel = this.state.showLabel;
	}

	/**
	 * Update the dropdown multi select state every time the checkbox changes state
	 */
	matCheckboxChange(event: MatCheckboxChange, option: Option) {
		if (!this.isMultiSelect) {
			this.singleSelectedItem = option;
		}

		if (event.checked) {
			if (!this.optionIsInSelectedOptions(option)) {
				const typedOption: CheckboxOption = {
					...option,
					checkboxElement: event.source,
				};

				this.potentiallySelectedOptions.push(typedOption);

				if (!this.isMultiSelect) {
					this.potentiallySelectedOptions = this.potentiallySelectedOptions.filter(op => {
						return op.value === this.singleSelectedItem.value;
					});
				}
			}
		} else {
			if (!this.isMultiSelect) {
				this.singleSelectedItem = null;
			}
			this.removeOptionFromSelectedOptions(option);
		}
	}

	/**
	 * Stop Event propagation to prevent the menu from collapsing on checkbox click
	 * @param event Mouse Event
	 */
	matCheckboxClick(event: MouseEvent) {
		event.stopPropagation();
	}

	searchInputClicked(event: MouseEvent) {
		event.stopPropagation();
	}

	/**
	 * Submit the dropdown selected values and update the state
	 */
	dropdownSubmit() {
		this.memCacheService.setValue("resetState", true);
		const submitEvent = this.potentiallySelectedOptions.map(option => option.value);

		this.submitDropdownMultiSelect.emit(submitEvent);
		if (!this.isMultiSelect) {
			this.submitDropdownSingleSelect.emit(this.singleSelectedItem?.value);
		}

		if (this.potentiallySelectedOptions.length === 0) {
			this.potentialFilterButtonText = this.dropdownLabel;
			this.showLabel = false;
			this.filterButtonTextClass = "filter-button-text-value-not-selected";
		} else if (this.potentiallySelectedOptions.length === 1) {
			this.potentialFilterButtonText = this.potentiallySelectedOptions[0].label;
			this.showLabel = true;
			this.filterButtonTextClass = "filter-button-text-value-selected";
		} else {
			this.potentialFilterButtonText = translateAndFormat("multiple", "capitalize");
			this.showLabel = true;
			this.filterButtonTextClass = "filter-button-text-value-selected";
		}

		this.selectedOption = this.potentialFilterButtonText;
		this.selectedOptions = [...this.potentiallySelectedOptions];

		this.flushState();

		this.showMenu = false;
	}

	/**
	 * Handle menu open
	 * Reset all the checkboxes, internally tracked state, and  check all the currently selected options.
	 */
	handleMenuOpen() {
		this.showMenu = true;
		this.buttonIconValue = "arrow_drop_up";
		this.filterButtonClass = "filter-button-focused";
		this.filterButtonLabelClass = "filter-button-label-focused";
		this.applyMenuPanelAria();

		this.defaultCheckState = {};

		this.singleSelectedItem = null;
		this.selectedOptions.forEach(option => {
			this.defaultCheckState[option.value] = true;
			this.singleSelectedItem = option;
		});

		this.searchInput?.reset();

		this.potentiallySelectedOptions = [...this.selectedOptions];

		// Move keyboard focus into the trap after the overlay has settled
		setTimeout(() => this.trapFocus?.focusTrap?.focusFirstTabbableElement());
	}

	private applyMenuPanelAria() {
		setTimeout(() => {
			const panelId = this.menuTrigger?.menu?.panelId;
			const panel = panelId ? (this.document.getElementById(panelId) as HTMLElement | null) : null;
			if (!panel) {
				return;
			}

			// The inner wrapper div carries role="dialog" and aria-modal="true".
			// Remove the conflicting role from the mat-menu overlay panel so screen
			// readers only encounter a single dialog region.
			panel.setAttribute("role", "presentation");
			panel.removeAttribute("aria-label");
		});
	}

	/**
	 * Handle menu close
	 */
	handleMenuClose() {
		this.buttonIconValue = "arrow_drop_down";
		this.filterButtonClass = "filter-button-not-focused";
		this.filterButtonLabelClass = "filter-button-label-not-focused";
		this.showMenu = false;
	}

	/**
	 * Intercept Tab/Shift+Tab within the dialog wrapper.
	 * Calling preventDefault() stops the browser from emitting focusout before
	 * focus moves — which is what causes mat-menu to close prematurely.
	 * We then route focus manually to the next/previous focusable element,
	 * piercing Shadow DOM boundaries (e.g. app-action-button uses ShadowDom encapsulation).
	 */
	handleMenuKeydown(event: KeyboardEvent): void {
		if (["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"].includes(event.key)) {
			const activeEl = this.getDeepActiveElement();
			const wrapper = this.dialogWrapperRef?.nativeElement;
			const container = wrapper?.querySelector(".option-checkbox-container");

			// Only intercept these keys if focus is within the listbox
			if (!container?.contains(activeEl)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const checkboxInputs = this.getCheckboxInputs();
			if (!checkboxInputs.length) return;

			const currentIndex = checkboxInputs.indexOf(activeEl as HTMLElement);

			if (event.key === "ArrowDown") {
				const next = currentIndex >= checkboxInputs.length - 1 ? 0 : currentIndex + 1;
				checkboxInputs[next].focus();
			} else if (event.key === "ArrowUp") {
				const prev = currentIndex <= 0 ? checkboxInputs.length - 1 : currentIndex - 1;
				checkboxInputs[prev].focus();
			} else if (event.key === "Home") {
				checkboxInputs[0].focus();
			} else if (event.key === "End") {
				checkboxInputs[checkboxInputs.length - 1].focus();
			} else if (event.key === "PageDown") {
				const next = Math.min(currentIndex + 8, checkboxInputs.length - 1);
				checkboxInputs[next].focus();
			} else if (event.key === "PageUp") {
				const prev = Math.max(currentIndex - 8, 0);
				checkboxInputs[prev].focus();
			}
			return;
		}

		if (event.key !== "Tab") return;

		event.preventDefault();
		event.stopPropagation();

		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return;

		const focusable: HTMLElement[] = [];
		this.collectFocusableElements(wrapper, focusable);

		if (!focusable.length) return;

		let activeEl = this.getDeepActiveElement() as HTMLElement;
		const container = wrapper.querySelector(".option-checkbox-container");
		if (container?.contains(activeEl)) {
			activeEl = container as HTMLElement;
		}

		const currentIndex = focusable.indexOf(activeEl);

		if (event.shiftKey) {
			const prev = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
			focusable[prev].focus();
		} else {
			const next = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
			focusable[next].focus();
		}
	}

	focusFirstOption(): void {
		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return;
		const container = wrapper.querySelector(".option-checkbox-container") as HTMLElement | null;
		if (!container) return;

		// Only steal focus on keyboard-driven focus (Tab into the listbox).
		// On mouse focus — including clicks on the gap/padding between options —
		// leave focus alone so the scroll position is preserved and the in-flight
		// click's mouseup lands on the same element as the mousedown.
		if (!container.matches(":focus-visible")) {
			return;
		}

		// Find the first checked checkbox
		const firstChecked = container.querySelector<HTMLElement>(
			"mat-checkbox.mat-checkbox-checked input[type='checkbox']:not([disabled]), mat-checkbox.mat-mdc-checkbox-checked input[type='checkbox']:not([disabled])",
		);
		if (firstChecked) {
			firstChecked.focus();
			return;
		}

		// Fallback to the very first option
		const checkboxInputs = this.getCheckboxInputs();
		if (checkboxInputs.length) {
			checkboxInputs[0].focus();
		}
	}

	private getCheckboxInputs(): HTMLElement[] {
		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return [];
		const container = wrapper.querySelector(".option-checkbox-container");
		if (!container) return [];
		return Array.from(container.querySelectorAll<HTMLElement>("input[type='checkbox']:not([disabled])"));
	}

	/**
	 * Walks the DOM tree in document order, recursing into Shadow roots so that
	 * focusable elements inside ShadowDom-encapsulated components (e.g. app-action-button)
	 * are included in the tab sequence.
	 */
	private collectFocusableElements(node: Element | ShadowRoot, result: HTMLElement[]): void {
		for (const child of Array.from(node.children)) {
			if (this.isFocusableElement(child as HTMLElement)) {
				result.push(child as HTMLElement);
			}
			if ((child as Element).shadowRoot) {
				this.collectFocusableElements((child as Element).shadowRoot, result);
			} else {
				this.collectFocusableElements(child as Element, result);
			}
		}
	}

	/**
	 * Returns true when an element should participate in the manual Tab sequence.
	 */
	private isFocusableElement(el: HTMLElement): boolean {
		if ((el as HTMLButtonElement).disabled) return false;
		const tabindex = el.getAttribute("tabindex");
		if (tabindex === "-1") return false;
		if (el.classList.contains("cdk-focus-trap-anchor")) return false;
		const tag = el.tagName.toLowerCase();
		if (["button", "input", "select", "textarea"].includes(tag)) return true;
		if (tag === "a" && el.hasAttribute("href")) return true;
		if (tabindex !== null) return true;
		return false;
	}

	/**
	 * Traverses shadow roots to find the truly focused element.
	 * document.activeElement only returns the shadow host when focus is inside a
	 * Shadow DOM — this helper drills down to the actual focused element.
	 */
	private getDeepActiveElement(): Element | null {
		let el: Element | null = document.activeElement;
		while (el?.shadowRoot?.activeElement) {
			el = el.shadowRoot.activeElement;
		}
		return el;
	}

	/**
	 * Update cashed dropdown state
	 */
	flushState() {
		this.memCacheService.setValue<DropdownMultiSelectState>(this.stateKey, {
			dropdownMultiSelectLabel: this.selectedOption,
			potentialDropdownMultiSelectLabel: this.potentialFilterButtonText,
			buttonIconValue: this.buttonIconValue,
			optionsSelected: [...this.selectedOptions],
			showLabel: this.showLabel,
		});
	}

	/**
	 * Checks if an option was already selected
	 * @param option Option object
	 * @returns True or False depending on whether the option is in the selected options array
	 */
	private optionIsInSelectedOptions(option: Option) {
		return this.potentiallySelectedOptions.find(potentialOption => potentialOption.value === option.value)
			? true
			: false;
	}

	/**
	 * Remove a given option from the potentiallySelectedOptions array
	 * @param option Option object
	 */
	private removeOptionFromSelectedOptions(option: Option) {
		this.potentiallySelectedOptions = this.potentiallySelectedOptions.filter(
			potentialOption => potentialOption.value !== option.value,
		);
	}

	/**
	 * Get current dropdown multi select state from cache
	 * @returns DropdownMultiSelectState object
	 */
	private getState() {
		return this.memCacheService.getValue<DropdownMultiSelectState>(this.stateKey);
	}

	/**
	 * Capture current dropdown multi select state
	 */
	private captureSelection() {
		this.initState();

		this.filterButtonTextClass =
			this.state.optionsSelected.length > 0
				? "filter-button-text-value-selected"
				: "filter-button-text-value-not-selected";

		this.potentiallySelectedOptions = [...this.state.optionsSelected];
		this.selectedOptions = [...this.state.optionsSelected];

		this.selectedOption = this.state.dropdownMultiSelectLabel;
	}

	isInspectionType() {
		return this.context && this.context.includes("inspectionType");
	}

	// maintains clicked checkbox state if user searches for "abc", clicks a result from "abc", clears search, clicks "def", clicks another result, from def, then clearing the search should bring them back to state of all options with 2 checkboxs filled in
	_setCheckboxesOnOriginalView() {
		this.subscriptions.add(
			this.checkboxes.changes.subscribe(changes => {
				const boxes = changes._results;
				boxes.forEach(box => {
					if (this.sortedBySearchIds[box.id]) {
						box.checked = true;
					}
				});
			}),
		);
	}

	inputChanged(search: string) {
		this.search = search && typeof search === "string" ? search.toLocaleLowerCase() : search;

		if (this.potentiallySelectedOptions.length) {
			this.sortedBySearchIds = this.potentiallySelectedOptions.reduce((acc, cur) => {
				acc[cur.value] = true;
				return acc;
			}, {});
		} else {
			this.sortedBySearchIds = {};
		}
		if (!this.search) {
			this.sortedOptionsBySearch = [];
			this.sortedOptions = this.originalOptions;

			this._setCheckboxesOnOriginalView();

			return;
		}

		this.sortedOptionsBySearch = this.originalOptions.filter(opt => {
			return opt.label.toLocaleLowerCase().includes(this.search);
		});
		this.sortedOptions = this.sortedOptionsBySearch;
	}
}
